import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import './LoginForm.css';

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await authAPI.login(username, password);

      if (res.data && res.data.access) {
        localStorage.setItem('token', res.data.access);
        localStorage.setItem('refresh_token', res.data.refresh);
        onLogin();
        window.location.reload();
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Login endpoint not found. Please check if the server is running.');
      } else if (err.response?.status === 401) {
        setError('Invalid username or password.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else if (err.code === 'ERR_NETWORK') {
        setError('Network error. Please check if the server is running.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const cards = document.querySelectorAll('.floating-card');

    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;

      cards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const cardX = rect.left + rect.width / 1;
        const cardY = rect.top + rect.height / 1;

        const deltaX = cardX - clientX;
        const deltaY = cardY - clientY;

        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const force = Math.min(500 / distance, 8);

        const moveX = deltaX * force * 0.10;
        const moveY = deltaY * force * 0.10;

        card.style.transform = `translate(${moveX}px, ${moveY}px)`;
      });
    };

    const resetCards = () => {
      cards.forEach(card => {
        card.style.transform = '';
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', resetCards);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', resetCards);
    };
  }, []);

  return (
    <div className="login-container">
      <div className="background-design">
        <div className="floating-card card-1">
          <div className="card-header">
            <div className="status-dot completed"></div>
            <span>Task Completed</span>
          </div>
          <div className="card-content">Graphic Design</div>
        </div>

        <div className="floating-card card-2">
          <div className="card-header">
            <div className="status-dot in-progress"></div>
            <span>In Progress</span>
          </div>
          <div className="card-content">Reel Creation</div>
        </div>

        <div className="floating-card card-3">
          <div className="card-header">
            <div className="status-dot pending"></div>
            <span>Pending</span>
          </div>
          <div className="card-content">Client Meeting</div>
        </div>

        <div className="floating-card card-4">
          <div className="card-header">
            <div className="status-dot completed"></div>
            <span>Done</span>
          </div>
          <div className="card-content">Web Development</div>
        </div>

         <div className="floating-card card-5">
          <div className="card-header">
            <div className="status-dot completed"></div>
            <span>Done</span>
          </div>
          <div className="card-content">Find References</div>
        </div>

        <div className="floating-card card-6">
          <div className="card-header">
            <div className="status-dot completed"></div>
            <span>Done</span>
          </div>
          <div className="card-content">Create Strategy</div>
        </div>

        <div className="geometric-shape shape-1"></div>
        <div className="geometric-shape shape-2"></div>
        <div className="geometric-shape shape-3"></div>
        <div className="geometric-shape shape-4"></div>
        <div className="geometric-shape shape-5"></div>
      </div>

      <div className="login-form-wrapper">
        <form className="login-form" onSubmit={handleLogin}>
          <div className="login-header">
            <div className="logo">
              <img
                src="https://eighteendigitalmarketing.com/wp-content/uploads/2025/07/eighteen-flow-logo@4x-scaled.png"
                alt="Eighteen Flow Logo"
                className="logo-image"
              />
            </div>
            <h2 className="login-title">Welcome Back</h2>
            <p className="login-subtitle">Sign in to manage your tasks efficiently</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              className="login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="password-wrapper">
              <input
                className="login-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button
            className="login-button"
            type="submit"
            disabled={isLoading}
            style={{ opacity: isLoading ? 0.7 : 1 }}
          >
            <span>{isLoading ? 'Signing In...' : 'Sign In'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
